import { useEffect, useRef, useState } from "react";
import { Box, Input as MuiInput } from "@mui/material";

import { Button } from "../../../components";
import { SingleSelectInput } from "../../../components/SelectInput";
import { ReactComponent as SearchIcon } from "../../../assets/svg/Search.svg";
import { colors } from "../../../theme/colors";
import {
  defaultFilters,
  DISCREPANCY_SELECT_OPTIONS,
  SEVERITY_SELECT_OPTIONS,
  SpecimenTrackingFilters,
  STATUS_TRANSITION_SELECT_OPTIONS,
} from "../utils";

type Props = {
  filters: SpecimenTrackingFilters;
  onChange: (filters: SpecimenTrackingFilters) => void;
};

const SEARCH_DEBOUNCE_MS = 300;

const FiltersBar = ({ filters, onChange }: Props) => {
  // The field is uncontrolled relative to `filters` while typing: keystrokes
  // update local state immediately, and only the settled value is pushed up so
  // the events/analytics queries refire once per pause instead of per keystroke.
  const [searchText, setSearchText] = useState(filters.search);

  // Read through refs inside the debounce so a pending timer commits against the
  // latest filters — otherwise picking a status mid-typing would be reverted by
  // a stale `filters` captured when the timer was scheduled.
  const filtersRef = useRef(filters);
  filtersRef.current = filters;
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  // Resync when search is changed from outside, e.g. "Clear filters".
  useEffect(() => {
    setSearchText(filters.search);
  }, [filters.search]);

  useEffect(() => {
    // Covers both mount and the resync above, so neither fires a redundant query.
    if (searchText === filtersRef.current.search) return;

    const timer = setTimeout(() => {
      onChangeRef.current({ ...filtersRef.current, search: searchText });
    }, SEARCH_DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [searchText]);

  return (
    <Box
      sx={{
        display: "flex",
        flexWrap: "wrap",
        alignItems: "center",
        gap: "16px",
      }}
    >
      <Box sx={{ flex: "1 1 280px", minWidth: 260 }}>
        <MuiInput
          disableUnderline
          fullWidth
          placeholder="Search QR code or Request Form ID"
          type="search"
          startAdornment={
            <SearchIcon width={20} height={20} style={{ marginRight: 8 }} />
          }
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          sx={{
            height: 48,
            px: "16px",
            borderRadius: "50px",
            border: `1px solid ${colors.grey}`,
            backgroundColor: colors.white,
            mb: 0
          }}
        />
      </Box>

      <Box sx={{ width: 220 }}>
        <SingleSelectInput
          placeholder="Status Transition"
          name="statusTransition"
          data={STATUS_TRANSITION_SELECT_OPTIONS}
          value={filters.statusTransition}
          onChange={(e) =>
            onChange({ ...filters, statusTransition: e.target.value as string })
          }
          onClear={() => onChange({ ...filters, statusTransition: "" })}
        />
      </Box>

      <Box sx={{ width: 150 }}>
        <SingleSelectInput
          placeholder="Discrepancy"
          name="discrepancy"
          data={DISCREPANCY_SELECT_OPTIONS}
          value={filters.discrepancy}
          onChange={(e) =>
            onChange({
              ...filters,
              discrepancy: e.target
                .value as SpecimenTrackingFilters["discrepancy"],
            })
          }
          onClear={() => onChange({ ...filters, discrepancy: "" })}
        />
      </Box>

      <Box sx={{ width: 160 }}>
        <SingleSelectInput
          placeholder="Severity"
          name="severity"
          data={SEVERITY_SELECT_OPTIONS}
          value={filters.severity}
          onChange={(e) =>
            onChange({ ...filters, severity: e.target.value as string })
          }
          onClear={() => onChange({ ...filters, severity: "" })}
        />
      </Box>

      <Button
        variant="outlined"
        label="Clear filters"
        onClick={() =>
          onChange({ ...defaultFilters(), healthOrgId: filters.healthOrgId })
        }
        sx={{ borderRadius: "8px", height: 48, px: 2.5, whiteSpace: "nowrap" }}
      />
    </Box>
  );
};

export default FiltersBar;
